import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";

function calcTotalPcs(expr) {
  const s = String(expr || "")
    .toLowerCase()
    .replaceAll(" ", "")
    .replaceAll("x", "*");

  if (s.includes("*")) {
    const [a, b] = s.split("*");
    const n1 = Number(a);
    const n2 = Number(b);
    if (Number.isFinite(n1) && Number.isFinite(n2)) return n1 * n2;
  }

  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function toYMD(val) {
  const s = String(val || "").slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return "";
}

function toExprFromTotalPcs(v) {
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : "";
}

function toArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function getMachineValue(m) {
  if (typeof m === "string") return m;
  return (
    m?.machine_name ||
    m?.machineName ||
    m?.name ||
    m?.machine ||
    m?.code ||
    ""
  );
}

function getItemCode(it) {
  return it?.item_code || it?.itemCode || it?.code || "";
}

function getItemName(it) {
  return it?.productName || it?.item_name || it?.itemName || it?.name || "";
}

function getItemType(it) {
  return String(
    it?.item_type || it?.itemType || it?.type || it?.department || ""
  ).toLowerCase();
}

function getEmpCode(emp) {
  return emp?.emp_code || emp?.empCode || emp?.code || "";
}

function getEmpName(emp) {
  return emp?.emp_name || emp?.empName || emp?.name || "";
}

export default function InjectionEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [productionDate, setProductionDate] = useState("");
  const [shift, setShift] = useState("Day");
  const [checkedBy, setCheckedBy] = useState("");
  const [receivedBy, setReceivedBy] = useState("");

  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);

        const [itemsRes, machinesRes, empRes] = await Promise.all([
          api.get(`/api/items?type=injection`),
          api.get(`/api/machines?type=injection`),
          api.get("/api/employees"),
        ]);

        let headerObj = null;
        try {
          const hRes = await api.get(`/api/injection/headers/${id}`);
          headerObj = hRes.data;
        } catch {
          const listRes = await api.get(`/api/injection/headers`);
          const headersArr = toArray(listRes.data);
          headerObj =
            headersArr.find((x) => String(x.headerId) === String(id)) || null;
        }

        const linesRes = await api.get(`/api/injection/headers/${id}/lines`);

        if (!alive) return;

        const itemsArr = toArray(itemsRes.data);
        const machinesArr = toArray(machinesRes.data);
        const employeesArr = toArray(empRes.data);
        const linesArr = toArray(linesRes.data);

        setItems(itemsArr);
        setMachines(machinesArr);
        setEmployees(employeesArr);

        setProductionDate(toYMD(headerObj?.productionDate));
        setShift(headerObj?.shift || "Day");
        setCheckedBy(headerObj?.checkedBy || "");
        setReceivedBy(headerObj?.receivedBy || "");

        const mapped = linesArr.map((l) => {
          const machine =
            l.machineName ?? l.machineNo ?? l.machine_name ?? "";

          const itemCode = l.itemCode ?? l.item_code ?? "";

          const itemName =
            l.productName ?? l.itemName ?? l.item_name ?? "";

          const totalPcs = Number(l.totalPcs ?? l.total_pcs ?? 0);
          const pcsWeight = Number(l.pcsWeight ?? l.pcs_weight ?? 0);
          const totalWeight =
            Number(l.totalWeight ?? l.total_weight ?? 0) ||
            totalPcs * pcsWeight;

          return {
            lineId: l.lineId ?? l.id ?? null,
            machine,
            itemCode,
            itemName,
            totalPcsExpr: toExprFromTotalPcs(totalPcs),
            pcsWeight: pcsWeight ? String(pcsWeight) : "",
            totalWeight,
          };
        });

        setRows(
          mapped.length
            ? mapped
            : [
                {
                  machine: "",
                  itemCode: "",
                  itemName: "",
                  totalPcsExpr: "",
                  pcsWeight: "",
                  totalWeight: 0,
                },
              ]
        );
      } catch (e) {
        alert(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            e.message ||
            "Failed to load entry"
        );
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id]);

  const injectionItems = useMemo(() => {
    return items.filter((i) => {
      const type = getItemType(i);
      if (!type) return true;
      return type.includes("injection") || type.includes("inj");
    });
  }, [items]);

  const itemMap = useMemo(() => {
    const map = new Map();
    injectionItems.forEach((i) => {
      const code = getItemCode(i);
      if (code) map.set(code, i);
    });
    return map;
  }, [injectionItems]);

  const machineOptions = useMemo(() => {
    return machines
      .map((m) => {
        const value = getMachineValue(m);
        return value ? { value, label: value } : null;
      })
      .filter(Boolean);
  }, [machines]);

  const itemCodeOptions = useMemo(() => {
    return injectionItems
      .map((it) => {
        const code = getItemCode(it);
        const name = getItemName(it);
        if (!code) return null;

        return {
          value: code,
          label: code,
          searchText: `${code} ${name}`.toLowerCase(),
        };
      })
      .filter(Boolean);
  }, [injectionItems]);

  const employeeOptions = useMemo(() => {
    return employees
      .map((emp) => {
        const empCode = getEmpCode(emp);
        const empName = getEmpName(emp);

        if (!empName) return null;

        return {
          value: empName,
          label: empName,
          searchText: `${empName} ${empCode}`.toLowerCase(),
        };
      })
      .filter(Boolean);
  }, [employees]);

  const grandTotalWeight = useMemo(() => {
    return rows.reduce((sum, r) => sum + num(r.totalWeight), 0);
  }, [rows]);

  function updateRow(index, key, value) {
    setRows((prev) => {
      const copy = [...prev];
      const row = { ...copy[index], [key]: value };

      if (key === "itemCode") {
        const item = itemMap.get(value);
        row.itemName = getItemName(item) || "";
      }

      const totalPcs = calcTotalPcs(row.totalPcsExpr);
      const pcsWeight = num(row.pcsWeight);
      row.totalWeight = totalPcs * pcsWeight;

      copy[index] = row;
      return copy;
    });
  }

  function addRow() {
    setRows((prev) => [
      ...prev,
      {
        machine: "",
        itemCode: "",
        itemName: "",
        totalPcsExpr: "",
        pcsWeight: "",
        totalWeight: 0,
      },
    ]);
  }

  function removeRow(index) {
    setRows((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== index)
    );
  }

  async function saveUpdate() {
    const lines = rows
      .map((r) => {
        const totalPcs = calcTotalPcs(r.totalPcsExpr);
        const pcsWeight = num(r.pcsWeight);
        const totalWeight = totalPcs * pcsWeight;

        return {
          lineId: r.lineId || undefined,
          machineName: r.machine,
          itemCode: r.itemCode,
          productName: r.itemName,
          totalPcs,
          pcsWeight,
          totalWeight,
        };
      })
      .filter(
        (x) =>
          x.machineName &&
          x.itemCode &&
          x.productName &&
          x.totalPcs > 0 &&
          x.pcsWeight > 0
      );

    if (!productionDate) return alert("Please select date.");
    if (!shift) return alert("Please select shift.");
    if (lines.length === 0) return alert("Fill at least 1 complete row.");

    try {
      setLoading(true);

      await api.put(`/api/injection/headers/${id}`, {
        productionDate,
        shift,
        checkedBy,
        receivedBy,
        lines,
      });

      alert("Updated successfully");
      navigate("/injection");
    } catch (err) {
      alert(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message ||
          "Update failed"
      );
    } finally {
      setLoading(false);
    }
  }

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "31px",
      height: "31px",
      border: "1px solid #ced4da",
      boxShadow: "none",
      borderRadius: "0.25rem",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "31px",
      padding: "0 8px",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0px",
      padding: "0px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "31px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      marginTop: "6px",
      marginBottom: "6px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      fontSize: "14px",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#212529",
    }),
  };

  if (loading) return <div className="p-3">Loading...</div>;

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2 className="mb-0">Edit Injection Entry (ID: {id})</h2>

        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-dark"
            onClick={saveUpdate}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Changes"}
          </button>

          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => navigate("/injection")}
          >
            Close
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Date</label>
              <input
                type="date"
                className="form-control"
                value={productionDate}
                onChange={(e) => setProductionDate(e.target.value)}
              />
            </div>

            <div className="col-md-2">
              <label className="form-label">Shift</label>
              <select
                className="form-select"
                value={shift}
                onChange={(e) => setShift(e.target.value)}
              >
                <option value="Day">Day</option>
                <option value="Night">Night</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Checked By</label>
              <Select
                options={employeeOptions}
                value={
                  employeeOptions.find((opt) => opt.value === checkedBy) || null
                }
                onChange={(selected) =>
                  setCheckedBy(selected ? selected.value : "")
                }
                placeholder="-- Select Employee --"
                isClearable
                isSearchable
                styles={customSelectStyles}
                noOptionsMessage={() => "No employee found"}
                filterOption={(option, inputValue) => {
                  const text =
                    `${option.label} ${option.data.searchText || ""}`.toLowerCase();
                  return text.includes(String(inputValue).toLowerCase());
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Received By</label>
              <Select
                options={employeeOptions}
                value={
                  employeeOptions.find((opt) => opt.value === receivedBy) || null
                }
                onChange={(selected) =>
                  setReceivedBy(selected ? selected.value : "")
                }
                placeholder="-- Select Employee --"
                isClearable
                isSearchable
                styles={customSelectStyles}
                noOptionsMessage={() => "No employee found"}
                filterOption={(option, inputValue) => {
                  const text =
                    `${option.label} ${option.data.searchText || ""}`.toLowerCase();
                  return text.includes(String(inputValue).toLowerCase());
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-bordered mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: 50 }}>Sr</th>
                <th style={{ width: 220 }}>Machine</th>
                <th style={{ width: 180 }}>Item Code</th>
                <th>Item Name</th>
                <th style={{ width: 140 }}>Total PCS</th>
                <th style={{ width: 140 }}>1 PCS Weight</th>
                <th style={{ width: 140 }}>Total Weight</th>
                <th style={{ width: 160 }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.lineId ?? idx}>
                  <td className="text-center align-middle">{idx + 1}</td>

                  <td className="align-middle">
                    <Select
                      options={machineOptions}
                      value={
                        machineOptions.find((opt) => opt.value === r.machine) ||
                        null
                      }
                      onChange={(selected) =>
                        updateRow(idx, "machine", selected ? selected.value : "")
                      }
                      placeholder="-- Select --"
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No machine found"}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td className="align-middle">
                    <Select
                      options={itemCodeOptions}
                      value={
                        itemCodeOptions.find((opt) => opt.value === r.itemCode) ||
                        null
                      }
                      onChange={(selected) =>
                        updateRow(
                          idx,
                          "itemCode",
                          selected ? selected.value : ""
                        )
                      }
                      placeholder="-- Select --"
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No item found"}
                      filterOption={(option, inputValue) => {
                        const text =
                          `${option.label} ${option.data.searchText || ""}`.toLowerCase();
                        return text.includes(String(inputValue).toLowerCase());
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td className="align-middle">
                    <input
                      className="form-control form-control-sm"
                      value={r.itemName}
                      readOnly
                    />
                  </td>

                  <td className="align-middle">
                    <input
                      className="form-control form-control-sm"
                      placeholder="500"
                      value={r.totalPcsExpr}
                      onChange={(e) =>
                        updateRow(idx, "totalPcsExpr", e.target.value)
                      }
                    />
                  </td>

                  <td className="align-middle">
                    <input
                      className="form-control form-control-sm"
                      placeholder="0.250"
                      value={r.pcsWeight}
                      onChange={(e) =>
                        updateRow(idx, "pcsWeight", e.target.value)
                      }
                    />
                  </td>

                  <td className="text-end fw-bold align-middle">
                    {num(r.totalWeight).toFixed(3)}
                  </td>

                  <td className="align-middle">
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeRow(idx)}
                      >
                        Remove
                      </button>

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-dark"
                        onClick={addRow}
                      >
                        + Add
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card-footer d-flex justify-content-between">
          <div>
            <b>Total Records:</b> {rows.length}
          </div>
          <div>
            <b>Grand Total Weight:</b> {grandTotalWeight.toFixed(3)}
          </div>
        </div>
      </div>
    </div>
  );
}